import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { logEvent } from '~/config/firebase.config';
import { subscribeVenueLanguagePicker, writeVenueLanguagePicker } from '~/services/playerLanguage/venueLanguagePicker';

import styles from './Account.redesign.module.css';
import own from './PlayerLanguageCard.module.css';

function IconGlobe() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <circle cx="12" cy="12" r="9" />
            <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
        </svg>
    );
}

/**
 * Carte « Téléphones des joueurs » (page Mon compte) : l'interrupteur « Laisser chaque
 * joueur choisir sa langue ». Éteint par défaut. Allumé, les drapeaux de langue
 * apparaissent sur le téléphone des joueurs (écran du pseudo, écran « Salut X »,
 * réglages en cours de partie). Enregistré tout de suite, sans bouton.
 * Réglage : `clients/{id}/public/gamepadSettings.languagePicker` (cf. le service).
 * Mesure : `account_player_language_toggle` (`enabled`).
 */
export function PlayerLanguageCard({ clientID, index }: { clientID: string; index: number }) {
    const { t } = useTranslation();
    // undefined = pas encore lu : interrupteur grisé.
    const [enabled, setEnabled] = useState<boolean | undefined>(undefined);
    const [error, setError] = useState<'load' | 'save' | null>(null);

    useEffect(() => {
        setEnabled(undefined);
        setError(null);
        return subscribeVenueLanguagePicker(
            clientID,
            setEnabled,
            // Suivi mort (Firestore n'enverra plus rien) : interrupteur grisé, message gardé.
            () => {
                setEnabled(undefined);
                setError('load');
            }
        );
    }, [clientID]);

    // Pas de verrou pendant l'écriture : hors connexion elle n'aboutit qu'au retour du
    // réseau, et le suivi en direct affiche déjà la nouvelle valeur (un 2e clic la
    // remet simplement). Écriture refusée : le suivi revient à la valeur enregistrée.
    const toggle = () => {
        if (enabled === undefined || error === 'load') return;
        const next = !enabled;
        setError(null);
        writeVenueLanguagePicker(clientID, next)
            .then(() => logEvent('account_player_language_toggle', 'Account', { clientID, enabled: next }))
            .catch(() => setError('save'));
    };

    return (
        <section className={styles.card} style={{ '--i': index } as React.CSSProperties}>
            <div className={styles.cardHead}>
                <div className={styles.cardIcon}>
                    <IconGlobe />
                </div>
                <div>
                    <div className={styles.cardTitle}>{t('account.playerLanguage.title')}</div>
                </div>
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={enabled === true}
                className={own.row}
                onClick={toggle}
                disabled={enabled === undefined}
            >
                <span className={own.rowLabel}>{t('account.playerLanguage.label')}</span>
                <span className={`${own.switch} ${enabled ? own.switchOn : ''}`} aria-hidden="true">
                    <span className={own.knob} />
                </span>
            </button>
            <p className={`${styles.hint} ${own.hint}`}>{t('account.playerLanguage.hint')}</p>
            {error && (
                <span className={`${styles.msg} ${styles.msgError}`} role="alert">
                    {t(error === 'load' ? 'account.playerLanguage.loadFailed' : 'account.updateFailed')}
                </span>
            )}
        </section>
    );
}
